import React, { useCallback, useMemo } from "react";
import { Formik, Form as FormWrapper, Field, FieldProps, FormikHelpers, FormikProps } from "formik";
import FormCheckbox from "components/Form/Checkbox";
import RadioGroup from "components/Form/RadioGroup";
import Grid from "components/primitives/Grid";
import CustomDateRangeFullWidthFix from "../CustomDateRangeFullWidthFix";
import DependantFormField from "../../Form/DependantFormField";
import CustomDateRange from "../../Form/CustomDateRange";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { schema } from "./ExportDataAthleteForm.schema";
import InputLabel from "@mui/material/InputLabel";
import FormAutoComplete from "components/Form/FormAutocomplete";
import { exerciseAutocompleteFilterOptions } from "config/autocomplete";
import Mui5 from "components/mui5";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import ColorableChip from "components/ColorableChip";
import { useArchivedAthletes } from "providers/ArchivedAthletesProvider";
import CanDoAction from "components/CanDoAction";
import useCanDo from "hooks/useCanDo";
import { useTranslation } from "react-i18next";
import AutocompleteOptionColourable from "components/AutocompleteOptionColourable";
import { Athlete, Circuit, EntityType, Exercise } from "types";
import { Group } from "types/groups";
import useNaturalSort from "hooks/useNaturalSort";

/** Only the identity/label fields the picker reads; the container passes full `Athlete`s. */
type AthleteOption = Pick<Athlete, "id" | "firstName" | "lastName">;
/** `athleteIds` is carried through to the schema cast for group selections. */
type GroupOption = Pick<Group, "id" | "name" | "colour" | "athleteIds">;
/** A circuit folded into the exercise picker with a translated group `category`. */
type CircuitOption = Circuit & { category: string };

/**
 * Raw Formik state: the multi-selects hold the chosen option objects (not ids)
 * until submit, when `schema.cast` reduces them — see {@link ExportDataAthleteFormSubmitValues}.
 */
interface ExportDataAthleteFormValues {
  athleteIds: (AthleteOption | GroupOption)[];
  exerciseIds: (Exercise | CircuitOption)[];
  startDate: string | null;
  endDate: string | null;
  period: string;
  splitExercises: boolean;
  includeHeight: boolean;
  includeWeight: boolean;
  includeWellness: boolean;
  includeEmail: boolean;
  includeSex: boolean;
  includeGroups: boolean;
  includeSports: boolean;
}

/** Payload passed to `onSubmit` after `schema.cast()`: selections reduced to ids, dates to ISO strings. */
interface ExportDataAthleteFormSubmitValues {
  athleteIds: string[];
  exerciseIds: string[];
  startDate: string;
  endDate: string;
  period: string;
  splitExercises: boolean;
  includeHeight: boolean;
  includeWeight: boolean;
  includeWellness: boolean;
  includeEmail: boolean;
  includeSex: boolean;
  includeGroups: boolean;
  includeSports: boolean;
}

interface ExportDataAthleteFormProps {
  onSubmit: (values: ExportDataAthleteFormSubmitValues, formikBag: FormikHelpers<ExportDataAthleteFormValues>) => void;
  athletes: AthleteOption[];
  groups: GroupOption[];
  exercises: Exercise[];
  circuits?: Circuit[];
}

const initialValues: ExportDataAthleteFormValues = {
  athleteIds: [],
  exerciseIds: [],
  startDate: null,
  endDate: null,
  period: "month",
  splitExercises: false,
  includeHeight: false,
  includeWeight: false,
  includeWellness: false,
  includeEmail: false,
  includeSex: false,
  includeGroups: false,
  includeSports: false,
};

function ExportDataAthleteForm(props: ExportDataAthleteFormProps) {
  const { onSubmit } = props;

  const archivedAthletes = useArchivedAthletes();
  const canDo = useCanDo();
  const { t } = useTranslation();
  const naturalCompare = useNaturalSort();

  const handleSubmit = useCallback(
    (values: ExportDataAthleteFormValues, formikBag: FormikHelpers<ExportDataAthleteFormValues>) => {
      /**
       * schema.cast() is used to convert
       * values to expected output. */
      const archivedAthleteIds = archivedAthletes.map((athlete) => athlete.id);
      const castValues = schema.cast(values) as ExportDataAthleteFormSubmitValues;

      return onSubmit(
        {
          ...castValues,
          athleteIds: castValues.athleteIds.filter((athleteId) => !archivedAthleteIds.includes(athleteId)),
        },
        formikBag
      );
    },
    [onSubmit, archivedAthletes]
  );

  const combinedAthletesAndGroups = React.useMemo(() => {
    return [
      ...props.athletes.map((athlete) => ({
        ...athlete,
        type: EntityType.ATHLETE,
        typeLabel: t("global.athlete_label_plural"),
      })),
      ...props.groups.map((group) => ({ ...group, type: EntityType.GROUP, typeLabel: t("global.groups_label") })),
    ];
  }, [t, props.athletes, props.groups]);

  const allAthletes = React.useMemo(
    () => combinedAthletesAndGroups.filter((item) => item.type === EntityType.ATHLETE),
    [combinedAthletesAndGroups]
  );
  const allGroups = React.useMemo(
    () => combinedAthletesAndGroups.filter((item) => item.type === EntityType.GROUP),
    [combinedAthletesAndGroups]
  );

  /**
   * Circuits are folded into the exercise picker as a single "Circuit" group.
   * Each circuit gets a translated `category` so it clusters under one header
   * (mirrors how wellness is folded into the RAG settings selector). The schema
   * extracts `.id` from every selection, so circuit ids flow through `exerciseIds`.
   */
  const exercisesAndCircuits = React.useMemo(() => {
    const circuitOptions = [...(props.circuits || [])]
      .sort((a, b) => naturalCompare(a.name, b.name))
      .map((circuit) => ({ ...circuit, category: t("global.metadata_type_Circuit") }));

    return [...props.exercises, ...circuitOptions];
  }, [props.exercises, props.circuits, naturalCompare, t]);

  const getOptionLabel = useCallback((option: any) => {
    return [option.firstName, option.lastName].filter(Boolean).join(" ") || option.name;
  }, []);

  const onCustomDateDependencyChange = React.useCallback(
    ({ setFieldValue }: FormikProps<any>) => setFieldValue("period", "custom"),
    []
  );

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
      <FormWrapper>
        <Box>
          <Grid container spacing="40px">
            <Grid size={12}>
              <Typography variant="h1" align="center">
                {t("export.form_title")}
              </Typography>
            </Grid>

            <Grid size={12}>
              <Field name="athleteIds">
                {({ field, form }: FieldProps) => (
                  <Mui5.Autocomplete
                    value={field.value}
                    onBlur={field.onBlur}
                    multiple
                    forcePopupIcon
                    autoHighlight
                    disableCloseOnSelect
                    options={combinedAthletesAndGroups}
                    groupBy={(option: any) => option.typeLabel}
                    filterSelectedOptions
                    getOptionLabel={(option: any) => getOptionLabel(option) || option.name}
                    onChange={(_, value) => form.setFieldValue("athleteIds", value)}
                    renderOption={(props, option: any) => (
                      <AutocompleteOptionColourable {...props} colour={option.colour} key={option.id} data-sensitive>
                        {getOptionLabel(option)}
                      </AutocompleteOptionColourable>
                    )}
                    renderInput={(params) => (
                      <Mui5.TextField
                        {...params}
                        name="groups"
                        color="secondary"
                        variant="standard"
                        label={t("export.select_athletes_or_groups_input_title", {
                          context: canDo("group.view") ? "groups" : undefined,
                        })}
                        error={Boolean(form.touched[field.name] && form.errors[field.name])}
                        helperText={
                          form.touched[field.name] && form.errors[field.name] && t(form.errors[field.name] as string)
                        }
                      />
                    )}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option: Group, index) => {
                        const { key, ...tagProps } = getTagProps({ index });

                        return (
                          <ColorableChip
                            key={option.id}
                            label={getOptionLabel(option)}
                            customColor={option.colour}
                            deleteIcon={
                              <ClearOutlinedIcon
                                titleAccess={t("export.remove_item_button_title_text")}
                                color={"primary"}
                                viewBox="-6 -6 36 36"
                              />
                            }
                            {...tagProps}
                            className="sr-no-capture"
                          />
                        );
                      })
                    }
                  />
                )}
              </Field>
              <Grid container spacing={0} p={0}>
                <Grid size="grow">
                  <Field name="athleteIds">
                    {({ field }: FieldProps) => (
                      <Mui5.Button
                        variant="text"
                        fullWidth
                        aria-label={t("export.select_all_athletes_button_text")}
                        onClick={() => field.onChange({ target: { name: field.name, value: allAthletes } })}
                        disabled={field?.value === allAthletes}
                      >
                        {t("export.select_all_athletes_button_text")}
                      </Mui5.Button>
                    )}
                  </Field>
                </Grid>
                <CanDoAction action="group.view">
                  <Grid size="grow">
                    <Field name="athleteIds">
                      {({ field }: FieldProps) => (
                        <Mui5.Button
                          variant="text"
                          fullWidth
                          aria-label={t("export.select_all_groups_button_text")}
                          onClick={() => field.onChange({ target: { name: field.name, value: allGroups } })}
                          disabled={field?.value === allGroups}
                        >
                          {t("export.select_all_groups_button_text")}
                        </Mui5.Button>
                      )}
                    </Field>
                  </Grid>
                </CanDoAction>
              </Grid>
            </Grid>
            <Grid size={12}>
              <FormAutoComplete
                multiple
                name="exerciseIds"
                limitTags={10}
                options={exercisesAndCircuits}
                groupBy={(option) => option.category}
                getOptionLabel={(option) => option.name}
                disableCloseOnSelect
                filterSelectedOptions
                filterOptions={exerciseAutocompleteFilterOptions}
                renderInput={(params) => (
                  <Mui5.TextField {...params} label={t("export.select_exercises_input_title")} variant="standard" />
                )}
                slotProps={{
                  chip: { variant: "outlined", color: "primary" },
                }}
              />
              <Field name="exerciseIds">
                {({ field }: FieldProps) => (
                  <Mui5.Button
                    variant="text"
                    fullWidth
                    aria-label={t("global.select_all_exercises_aria_label")}
                    onClick={() => field.onChange({ target: { name: field.name, value: exercisesAndCircuits } })}
                    disabled={field?.value === exercisesAndCircuits}
                  >
                    {t("export.select_exercises_button_text")}
                  </Mui5.Button>
                )}
              </Field>
            </Grid>
            <Grid size={12}>
              <InputLabel focused={false} shrink>
                {t("export.other_exports_input_title")}
              </InputLabel>
              <Grid container>
                <Grid size={6}>
                  <FormCheckbox color="primary" label={t("global.email_address_text")} name="includeEmail" />
                </Grid>
                <Grid size={6}>
                  <FormCheckbox color="primary" label={t("athlete.form_label_sex")} name="includeSex" />
                </Grid>
                <Grid size={6}>
                  <FormCheckbox color="primary" label={t("global.groups_label")} name="includeGroups" />
                </Grid>
                <Grid size={6}>
                  <FormCheckbox
                    color="primary"
                    label={t("export.other_exports_sports_checkbox_label")}
                    name="includeSports"
                  />
                </Grid>
                <Grid size={6}>
                  <FormCheckbox
                    color="primary"
                    label={t("export.other_exports_height_checkbox_label")}
                    name="includeHeight"
                  />
                </Grid>
                <Grid size={6}>
                  <FormCheckbox
                    color="primary"
                    label={t("export.other_exports_weight_checkbox_label")}
                    name="includeWeight"
                  />
                </Grid>
                <Grid size={6}>
                  <FormCheckbox
                    color="primary"
                    label={t("export.other_exports_wellness_checkbox_label")}
                    name="includeWellness"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid size={12}>
              <Box>
                <RadioGroup
                  label={t("export.date_range_input_title")}
                  name="period"
                  color="primary"
                  options={[
                    { label: t("global.time_label_today"), value: "day" },
                    { label: t("global.time_label_week"), value: "week" },
                    { label: t("global.time_label_month"), value: "month" },
                    { label: t("global.time_label_all_time"), value: "all" },
                    {
                      label: t("global.time_label_custom"),
                      value: "custom",
                      component: useMemo(
                        () => (
                          <CustomDateRangeFullWidthFix>
                            <DependantFormField
                              dependencyName="startDate"
                              onDependencyChange={onCustomDateDependencyChange}
                            />
                            <DependantFormField
                              dependencyName="endDate"
                              onDependencyChange={onCustomDateDependencyChange}
                            />
                            <CustomDateRange startInputName="startDate" endInputName="endDate" />
                          </CustomDateRangeFullWidthFix>
                        ),
                        [onCustomDateDependencyChange]
                      ),
                    },
                  ]}
                />
              </Box>
            </Grid>
            <Grid size={12}>
              <InputLabel focused={false} shrink>
                {t("global.form_options_title")}
              </InputLabel>
              <FormCheckbox color="primary" label={t("export.split_exercises_option_label")} name="splitExercises" />
            </Grid>
            <Grid size={12}>
              <Mui5.Button
                fullWidth
                type="submit"
                role="button"
                variant="contained"
                color="primary"
                disableElevation
                aria-label={t("export.export_data_button_text")}
              >
                {t("export.export_data_button_text")}
              </Mui5.Button>
            </Grid>
          </Grid>
        </Box>
      </FormWrapper>
    </Formik>
  );
}

export default ExportDataAthleteForm;
